import React, { useState } from 'react';
import { Sheet } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { hasGoogleToken, openGoogleAuthPopup, googleSheetsService } from '@/infrastructure/google';
import { GoogleSheetsExportModal } from './GoogleSheetsExportModal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

type SheetsButtonState = 'idle' | 'connecting' | 'creating' | 'done' | 'error';

interface GoogleSheetsExportButtonProps {
  recentCampaigns: any[];
  topInfluencers: any[];
  paymentSummary: any;
}

export const GoogleSheetsExportButton: React.FC<GoogleSheetsExportButtonProps> = ({
  recentCampaigns,
  topInfluencers,
  paymentSummary,
}) => {
  const [sheetsState, setSheetsState] = useState<SheetsButtonState>('idle');
  const [sheetUrl, setSheetUrl] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const handleShareToSheets = async () => {
    try {
      const isConnected = await hasGoogleToken();

      if (!isConnected) {
        setSheetsState('connecting');
        await openGoogleAuthPopup();
      }

      setSheetsState('creating');

      const url = await googleSheetsService.createSheet({
        campaigns: recentCampaigns || [],
        topInfluencers: topInfluencers || [],
        paymentSummary: paymentSummary || null,
      });

      setSheetUrl(url);
      setSheetsState('done');
      setShowModal(true);
    } catch (err: any) {
      setSheetsState('error');
      toast({
        title: 'Google Sheets failed',
        description: err.message || 'Something went wrong. Please try again.',
        variant: 'destructive',
      });
      setTimeout(() => setSheetsState('idle'), 3000);
    }
  };

  const sheetsButtonLabel: Record<SheetsButtonState, string> = {
    idle: 'Share via Google Sheets',
    connecting: 'Connecting to Google...',
    creating: 'Preparing Sheet...',
    done: 'Sheet Ready',
    error: 'Try Again',
  };

  return (
    <>
      <button
        id="share-google-sheets-btn"
        className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
          sheetsState === 'done'
            ? 'text-white bg-green-500 hover:bg-green-600'
            : sheetsState === 'error'
              ? 'text-white bg-red-500 hover:bg-red-600'
              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
        }`}
        onClick={handleShareToSheets}
        disabled={sheetsState === 'connecting' || sheetsState === 'creating'}
        aria-busy={sheetsState === 'connecting' || sheetsState === 'creating'}
      >
        {(sheetsState === 'connecting' || sheetsState === 'creating') ? (
          <LoadingSpinner size="small" iconClassName="text-current" />
        ) : (
          <Sheet className="w-4 h-4" />
        )}
        {sheetsButtonLabel[sheetsState]}
      </button>

      {showModal && sheetUrl && (
        <GoogleSheetsExportModal
          sheetUrl={sheetUrl}
          onClose={() => { setShowModal(false); setSheetsState('idle'); }}
        />
      )}
    </>
  );
};
